import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Target, Users, Zap, BarChart3, Handshake } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/projects/${slug}`)
      .then((r) => setProject(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 gap-4">
        <p className="font-body text-slate-400">Projet non trouve</p>
        <Link to="/projets" className="text-blue-400 font-body text-sm">Retour aux projets</Link>
      </div>
    );
  }

  const sections = [
    { icon: Target, title: "Objectifs", content: project.objectives },
    { icon: Users, title: "Public cible", content: project.target_audience },
    { icon: Zap, title: "Actions realisees", content: project.actions },
    { icon: BarChart3, title: "Resultats", content: project.results },
  ];

  return (
    <div data-testid="project-detail-page" className="pt-24">
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${project.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 to-slate-950" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/projets" className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 font-body text-sm mb-8 transition-colors" data-testid="back-to-projects">
            <ArrowLeft className="w-4 h-4" /> Retour aux projets
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-6 leading-[1.1]">
            {project.title}
          </h1>
          <p className="font-body text-base md:text-lg font-light text-slate-300 max-w-3xl">
            {project.context}
          </p>
        </div>
      </section>

      <div className="section-divider" />

      {/* KPIs */}
      {project.kpis && project.kpis.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap gap-4 justify-center">
              {project.kpis.map((kpi, i) => (
                <div key={i} className="bg-blue-500/10 border border-blue-500/20 rounded-full px-6 py-3">
                  <span className="font-body text-sm text-blue-400 font-medium">{kpi}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="section-divider" />

      {/* Content sections */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          {sections.map((s) => (
            <div key={s.title} className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-50">{s.title}</h3>
              </div>
              <p className="font-body text-base text-slate-300 leading-relaxed">{s.content}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Partners */}
      {project.partners && project.partners.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-6">
              <Handshake className="w-5 h-5 text-blue-400" />
              <h3 className="font-heading text-xl font-medium text-slate-50">Partenaires impliques</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {project.partners.map((p, i) => (
                <span key={i} className="font-body text-sm bg-white/5 border border-white/10 text-slate-300 px-4 py-2 rounded-full">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
